import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import type { Product } from "@/api/types";
import { Input, tx } from "@/components/ui";

export type ProductLine = { product: Product; qty: number; unit_price: string };
const money = (v: string | number) => (Math.round(Number(v) * 100) / 100).toFixed(2);
export const linesTotal = (lines: ProductLine[]) => Math.round(lines.reduce((acc, l) => acc + l.qty * Number(l.unit_price || 0), 0) * 100) / 100;

/** Product lines sold at a check-out or in a stand-alone sale: search, quantity, the price of the moment (editable). */
export function ProductLines({ brandId, lines, onChange }: { brandId: string; lines: ProductLine[]; onChange: (l: ProductLine[]) => void }) {
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState("");
  const res = useQuery({ queryKey: ["products", brandId, "search", q], queryFn: () => api<Product[]>(`/app/products?brand_id=${brandId}&active=true&q=${encodeURIComponent(q)}&limit=8`), enabled: q.length >= 1 });
  const add = (p: Product) => {
    const i = lines.findIndex((l) => l.product.id === p.id);
    onChange(i >= 0 ? lines.map((l, j) => (j === i ? { ...l, qty: l.qty + 1 } : l)) : [...lines, { product: p, qty: 1, unit_price: money(p.selling_price) }]);
    setQ("");
  };
  const set = (i: number, patch: Partial<ProductLine>) => onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <div className="space-y-1">
      <div className="text-xs text-ink">{t("retail.products")}</div>
      {lines.map((l, i) => (
        <div key={l.product.id} className="flex items-center gap-2 text-sm">
          <span className="min-w-0 flex-1 truncate">{tx(l.product.name, i18n.language)}{l.product.stock_qty <= l.qty ? <span className="ml-1 text-xs text-warn">({t("retail.stock")}: {l.product.stock_qty})</span> : null}</span>
          <Input className="!w-16 text-center" type="number" min={1} value={l.qty} onChange={(e) => set(i, { qty: Math.max(1, Number(e.target.value)) })} />
          <span className="text-muted">×</span>
          <Input className="!w-24" type="number" step="0.01" min="0" value={l.unit_price} onChange={(e) => set(i, { unit_price: e.target.value })} />
          <span className="w-16 text-right font-mono">{money(l.qty * Number(l.unit_price || 0))}</span>
          <button type="button" className="text-xs" aria-label={t("app.remove")} onClick={() => onChange(lines.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <div className="relative">
        <Input placeholder={t("retail.addProduct")} value={q} onChange={(e) => setQ(e.target.value)} />
        {q && res.data && res.data.length > 0 && (
          <ul className="absolute z-10 mt-1 max-h-40 w-full divide-y overflow-auto rounded-lg border bg-surface text-sm shadow-card">
            {res.data.map((p) => <li key={p.id}><button type="button" className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-surface-2" onClick={() => add(p)}><span>{tx(p.name, i18n.language)}{p.sku ? <span className="ml-1 text-xs text-muted">{p.sku}</span> : null}</span><span className="font-mono text-xs">{money(p.selling_price)} € · {p.stock_qty}</span></button></li>)}
          </ul>
        )}
        {q && res.data && res.data.length === 0 && <p className="mt-1 text-xs text-muted">{t("retail.noProduct")}</p>}
      </div>
      {lines.length > 0 && <div className="text-right text-xs text-muted">{t("retail.productsTotal")}: <b className="text-ink">{money(linesTotal(lines))} €</b></div>}
    </div>
  );
}
